import React, { useState } from "react";
import axios from "axios";
import config from "../../config";
import Toast from "../Common/Toast";

const formatDateForApi = (dateValue) => {
    if (!dateValue) return "";
    return dateValue;
};

const formatDateForDisplay = (dateValue) => {
    if (!dateValue) return "";
    const parts = String(dateValue).split("-");
    if (parts.length !== 3) return String(dateValue);
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
};

const mapTradeType = (tradeType) => {
    if (tradeType === "B") return "Buy";
    if (tradeType === "S") return "Sell";
    return tradeType || "";
};

const ReconciliationReport = ({ onFix }) => {
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [hasFetched, setHasFetched] = useState(false);
    const [error, setError] = useState("");
    const [fixedRowKeys, setFixedRowKeys] = useState({});
    const [fixingRowKeys, setFixingRowKeys] = useState({});
    const [toast, setToast] = useState({ show: false, message: "", type: "info" });

    const getRowKey = (row, index) => `${row?.[12] || row?.[6] || "txn"}-${index}`;

    const fetchReconciliationReport = async () => {
        setError("");
        setRows([]);
        setHasFetched(false);

        if (!fromDate || !toDate) {
            setHasFetched(true);
            setError("Please select both From Date and To Date.");
            return;
        }

        try {
            setIsLoading(true);
            const response = await axios.get(
                config.BL_API_URL + "/eodProcesses/v1/reconciliationReport",
                {
                    params: {
                        fromDate: formatDateForApi(fromDate),
                        toDate: formatDateForApi(toDate)
                    }
                }
            );

            setRows(Array.isArray(response.data) ? response.data : []);
            setHasFetched(true);
            setFixedRowKeys({});
            setFixingRowKeys({});
            setToast({ show: false, message: "", type: "info" });
        } catch (err) {
            setRows([]);
            setHasFetched(true);
            setError("Unable to load reconciliation report.");
            console.log(err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleFixClick = async (row, index) => {
        if (typeof onFix !== "function") return;

        const rowKey = getRowKey(row, index);
        if (fixedRowKeys[rowKey] || fixingRowKeys[rowKey]) return;

        setFixingRowKeys((prev) => ({ ...prev, [rowKey]: true }));
        const result = await onFix(row);
        const isSuccess = typeof result === "object" ? !!result?.success : !!result;
        const resultMessage =
            typeof result === "object"
                ? result?.message
                : isSuccess
                    ? "Transaction added successfully."
                    : "Failed to add transaction.";

        if (isSuccess) {
            setFixedRowKeys((prev) => ({ ...prev, [rowKey]: true }));
            setToast({
                show: true,
                type: "success",
                message: resultMessage || "Transaction added successfully."
            });
        } else {
            setToast({
                show: true,
                type: "error",
                message: resultMessage || "Failed to add transaction."
            });
        }

        setFixingRowKeys((prev) => {
            const next = { ...prev };
            delete next[rowKey];
            return next;
        });
    };

    return (
        <div className="w-full">
            <Toast
                show={toast.show}
                message={toast.message}
                type={toast.type}
                onClose={() => setToast({ show: false, message: "", type: "info" })}
            />
            <div className="flex flex-wrap gap-3 items-end mb-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                    <input
                        type="date"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        className="border border-gray-300 rounded px-3 py-2"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                    <input
                        type="date"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        className="border border-gray-300 rounded px-3 py-2"
                    />
                </div>
                <button
                    type="button"
                    onClick={fetchReconciliationReport}
                    disabled={isLoading}
                    className="bg-gray-700 hover:bg-gray-800 text-white font-bold py-2 px-4 rounded"
                >
                    {isLoading ? "Loading..." : "Execute"}
                </button>
            </div>

            {error && <div className="text-red-600 text-sm mb-3">{error}</div>}

            {hasFetched && rows.length === 0 && !error && (
                <div className="text-gray-700 font-medium">
                    no Missing trade data to show for given dates.
                </div>
            )}

            {rows.length > 0 && (
                <div className="overflow-auto border border-gray-200 rounded">
                    <table className="min-w-full text-sm text-left">
                        <thead className="bg-gray-100 text-gray-700">
                            <tr>
                                <th className="px-3 py-2">Date</th>
                                <th className="px-3 py-2">Batch ID</th>
                                <th className="px-3 py-2">Client Code</th>
                                <th className="px-3 py-2">Scrip Code</th>
                                <th className="px-3 py-2">Transaction ID</th>
                                <th className="px-3 py-2">Trade Type</th>
                                <th className="px-3 py-2">Quantity</th>
                                <th className="px-3 py-2">Unique Number</th>
                                <th className="px-3 py-2">Status</th>
                                <th className="px-3 py-2">Message</th>
                                <th className="px-3 py-2">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, index) => {
                                const rowKey = getRowKey(row, index);
                                const isFixed = !!fixedRowKeys[rowKey];
                                const isFixing = !!fixingRowKeys[rowKey];

                                return (
                                <tr key={rowKey} className="border-t border-gray-100">
                                    <td className="px-3 py-2">{formatDateForDisplay(row?.[0])}</td>
                                    <td className="px-3 py-2">{row?.[3]}</td>
                                    <td className="px-3 py-2">{row?.[4]}</td>
                                    <td className="px-3 py-2">{row?.[5]}</td>
                                    <td className="px-3 py-2">{row?.[6]}</td>
                                    <td className="px-3 py-2">{mapTradeType(row?.[7])}</td>
                                    <td className="px-3 py-2">{row?.[8]}</td>
                                    <td className="px-3 py-2">{row?.[12]}</td>
                                    <td className="px-3 py-2">{row?.[13]}</td>
                                    <td className="px-3 py-2">{row?.[14]}</td>
                                    <td className="px-3 py-2">
                                        <button
                                            type="button"
                                            onClick={() => handleFixClick(row, index)}
                                            disabled={isFixed || isFixing}
                                            className={`text-white px-3 py-1 rounded ${isFixed || isFixing ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"}`}
                                        >
                                            {isFixing ? "Fixing..." : isFixed ? "Fixed" : "Fix"}
                                        </button>
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default ReconciliationReport;
